import type { RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { AttachmentPreviewGrid } from '@/components/shared/AttachmentPreviewGrid'
import { FileUploadDropzone } from '@/components/shared/FileUploadDropzone'
import type { AttachmentRef } from '@/components/shared'
import { DatePicker } from '@/components/shared/DatePicker'
import { CurrencyInput } from '@/components/ui/currency-input'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_FOCUS_WITHIN_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { formatCurrency } from '@/lib/format'
import { cn } from '@/lib/utils'
import {
  PAYMENT_METHOD_LABELS,
  type CreatePaymentMethod,
  type PaymentMethod,
  type PaymentStatus,
} from '@/types'
import { PAYMENT_PROOF_ACCEPTED_EXT } from './paymentProofPreview'

const METHOD_OPTIONS: CreatePaymentMethod[] = [
  'cash',
  'transfer',
  'cheque',
  'card',
]

const SORTED_METHOD_OPTIONS = [...METHOD_OPTIONS].sort((first, second) =>
  PAYMENT_METHOD_LABELS[first].localeCompare(PAYMENT_METHOD_LABELS[second])
)

const STATUS_OPTIONS: PaymentStatus[] = [
  'pending',
  'paid',
  'failed',
  'refunded',
]

const inputFieldClass = cn(
  'h-10 rounded-xl border-[#e7e9ef] bg-[#f4f5f8] font-sans text-sm font-normal leading-[1.4] text-[#171a20] placeholder:font-normal placeholder:text-[#6c7285]',
  FORM_CONTROL_FOCUS_CLASS
)

const textareaFieldClass =
  'font-sans text-sm font-normal leading-[1.4] text-[#171a20] placeholder:font-normal placeholder:text-[#6c7285]'

const selectFieldClass = cn(
  'h-10 rounded-xl border-[#e7e9ef] bg-[#f4f5f8] font-sans text-sm font-normal leading-[1.4] text-[#171a20] placeholder:font-normal data-[placeholder]:font-normal data-[placeholder]:text-[#6c7285] [&>span]:font-normal data-[placeholder]:[&>span]:font-normal [&>span>span]:font-normal data-[placeholder]:[&>span>span]:font-normal',
  FORM_CONTROL_FOCUS_CLASS
)

const dateFieldClass = `${inputFieldClass} [&_input]:font-sans [&_input]:text-sm [&_input]:font-normal [&_input]:leading-[1.4] [&_input]:text-[#171a20] [&_input]:placeholder:text-[#6c7285] data-[has-value=true]:[&_input]:font-normal`

const invoiceInputTypographyClass =
  'text-sm font-normal text-[#171a20] placeholder:font-normal placeholder:text-[#6c7285]'

const invoiceSelectTypographyClass =
  'text-sm font-normal text-[#171a20] data-[placeholder]:text-[#6c7285] [&>span]:font-normal [&>span>span]:font-normal'

const invoiceDateTypographyClass =
  '[&_input]:text-sm [&_input]:font-normal [&_input]:text-[#171a20] [&_input]:placeholder:text-[#6c7285] data-[has-value=true]:[&_input]:font-normal'

export type PaymentFormErrors = {
  client_id?: string
  amount?: string
  method?: string
  paid_date?: string
  proof?: string
  status?: string
}

type PaymentFormFieldLayout = 'drawer' | 'invoice'

export type PaymentFormFieldsProps = {
  layout: PaymentFormFieldLayout
  amount: string
  method: PaymentMethod | ''
  status: PaymentStatus | ''
  paidDate: string
  reference: string
  proofInputRef: RefObject<HTMLInputElement | null>
  proofPreviewItems: AttachmentRef[]
  proofDragActive: boolean
  proofsError?: string
  notes: string
  waiting: { id: string; url: string } | null
  errors: PaymentFormErrors
  overpayment: boolean
  outstanding: number | null
  showStatusField: boolean
  statusLocked: boolean
  showReference: boolean
  portalContainer: HTMLDivElement | null
  onAmountChange: (value: string) => void
  onMethodChange: (value: PaymentMethod) => void
  onStatusChange: (value: PaymentStatus) => void
  onPaidDateChange: (value: string) => void
  onReferenceChange: (value: string) => void
  onPickProofs: (files: FileList | File[] | null) => void
  onProofDragActiveChange: (active: boolean) => void
  onRemoveProof: (id: string) => void
  onRenameProof: (item: AttachmentRef, name: string) => void
  onNotesChange: (value: string) => void
}

export function PaymentFormFields({
  layout,
  amount,
  method,
  status,
  paidDate,
  reference,
  proofInputRef,
  proofPreviewItems,
  proofDragActive,
  proofsError,
  notes,
  waiting,
  errors,
  overpayment,
  outstanding,
  showStatusField,
  statusLocked,
  showReference,
  portalContainer,
  onAmountChange,
  onMethodChange,
  onStatusChange,
  onPaidDateChange,
  onReferenceChange,
  onPickProofs,
  onProofDragActiveChange,
  onRemoveProof,
  onRenameProof,
  onNotesChange,
}: PaymentFormFieldsProps) {
  const { t } = useTranslation('payments')
  const fieldLabelClass = cn(
    'font-sans text-xs font-medium leading-4 text-foreground',
    layout === 'invoice' && 'text-[#171a20]'
  )
  const fieldBorderClass = 'border-transparent'
  const amountFieldMessage = overpayment
    ? t('form.overpaymentNotice', {
        amount: formatCurrency(outstanding ?? 0),
      })
    : errors.amount

  const amountField = (
    <div className='space-y-1.5'>
      <Label className={fieldLabelClass}>
        {t('form.amount')} <span className='text-destructive'>*</span>
      </Label>
      <CurrencyInput
        value={amount}
        onValueChange={onAmountChange}
        placeholder={t('form.amountPlaceholder')}
        showEuroPrefix
        className={cn(
          inputFieldClass,
          layout === 'invoice' && invoiceInputTypographyClass,
          fieldBorderClass,
          (errors.amount || overpayment) && 'border-destructive'
        )}
        prefixClassName={
          layout === 'invoice'
            ? 'text-sm font-normal text-[#171a20]'
            : 'font-sans text-sm font-normal text-[#171a20]'
        }
      />
      {amountFieldMessage && (
        <p className='text-xs text-destructive'>{amountFieldMessage}</p>
      )}
    </div>
  )

  const methodField = (
    <div className='space-y-1.5'>
      <Label className={fieldLabelClass}>
        {t('form.paymentMethod')} <span className='text-destructive'>*</span>
      </Label>
      <Select
        value={method}
        onValueChange={value => onMethodChange(value as PaymentMethod)}
      >
        <SelectTrigger
          className={cn(
            selectFieldClass,
            layout === 'invoice' && invoiceSelectTypographyClass,
            fieldBorderClass,
            errors.method && 'border-destructive'
          )}
        >
          <SelectValue placeholder={t('form.selectMethod')} />
        </SelectTrigger>
        <SelectContent container={portalContainer}>
          {SORTED_METHOD_OPTIONS.map(option => (
            <SelectItem
              key={option}
              value={option}
              className={cn(
                layout === 'invoice' &&
                  'font-normal text-[#171a20] data-[state=checked]:font-medium'
              )}
            >
              {PAYMENT_METHOD_LABELS[option]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {errors.method && (
        <p className='text-xs text-destructive'>{errors.method}</p>
      )}
    </div>
  )

  const statusField = (
    <div className='space-y-1.5'>
      <Label className={fieldLabelClass}>{t('form.status')}</Label>
      <Select
        value={status}
        disabled={statusLocked}
        onValueChange={value => onStatusChange(value as PaymentStatus)}
      >
        <SelectTrigger
          className={cn(
            selectFieldClass,
            fieldBorderClass,
            errors.status && 'border-destructive'
          )}
          disabled={statusLocked}
        >
          <SelectValue placeholder={t('updateStatusDialog.selectStatus')} />
        </SelectTrigger>
        <SelectContent container={portalContainer}>
          {STATUS_OPTIONS.map(option => (
            <SelectItem key={option} value={option}>
              {t(`statusOptions.${option}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {errors.status && (
        <p className='text-xs text-destructive'>{errors.status}</p>
      )}
    </div>
  )

  const paidDateField = (
    <div className='space-y-1.5'>
      <Label className={fieldLabelClass}>
        {layout === 'invoice' ? t('form.payDate') : t('form.collectionDate')}{' '}
        <span className='text-destructive'>*</span>
      </Label>
      <DatePicker
        value={paidDate}
        onChange={onPaidDateChange}
        className={cn(
          dateFieldClass,
          layout === 'invoice' && invoiceDateTypographyClass,
          fieldBorderClass,
          FORM_CONTROL_FOCUS_WITHIN_CLASS
        )}
        error={!!errors.paid_date}
        variant='field'
      />
      {errors.paid_date && (
        <p className='text-xs text-destructive'>{errors.paid_date}</p>
      )}
    </div>
  )

  return (
    <>
      {layout === 'invoice' ? (
        <div className='grid gap-4 lg:grid-cols-3'>
          {amountField}
          {methodField}
          {paidDateField}
        </div>
      ) : (
        <>
          <div className='grid gap-4 sm:grid-cols-2'>
            {amountField}
            {methodField}
          </div>
          <div className='grid gap-4 sm:grid-cols-2'>
            {showStatusField && statusField}
            {paidDateField}
          </div>
        </>
      )}

      {showReference && (
        <div className='space-y-1.5'>
          <Label className={fieldLabelClass}>{t('form.referenceNumber')}</Label>
          <Input
            value={reference}
            maxLength={50}
            placeholder={t('form.referencePlaceholder')}
            onChange={event => onReferenceChange(event.target.value)}
            className={cn(
              inputFieldClass,
              layout === 'invoice' && invoiceInputTypographyClass,
              fieldBorderClass
            )}
          />
        </div>
      )}

      {method === 'card' && (
        <p className='rounded-xl bg-[#eef4ff] px-3 py-2 text-xs text-foreground'>
          {t('form.stripeNotice')}
        </p>
      )}

      <div className='space-y-3'>
        {layout !== 'invoice' && (
          <Label className={fieldLabelClass}>{t('form.proofOfPayment')}</Label>
        )}
        <input
          ref={proofInputRef}
          type='file'
          multiple
          accept={PAYMENT_PROOF_ACCEPTED_EXT}
          className='hidden'
          onChange={event => {
            onPickProofs(event.target.files)
            event.target.value = ''
          }}
        />
        <FileUploadDropzone
          onClick={() => proofInputRef.current?.click()}
          onDragEnter={event => {
            event.preventDefault()
            onProofDragActiveChange(true)
          }}
          onDragOver={event => event.preventDefault()}
          onDragLeave={event => {
            event.preventDefault()
            onProofDragActiveChange(false)
          }}
          onDrop={event => {
            event.preventDefault()
            onProofDragActiveChange(false)
            onPickProofs(event.dataTransfer.files)
          }}
          primaryText={t('form.proofDropzonePrimary')}
          secondaryText={t('form.proofDropzoneFormats')}
          dragActive={proofDragActive}
          hasError={!!proofsError}
          className={cn(
            '[&>span:last-child>span]:font-normal [&>span:last-child>span]:leading-[1.4]',
            layout === 'invoice' &&
              '[&>span:last-child>span:first-child]:font-medium [&>span:last-child>span:first-child]:text-[#171a20]'
          )}
        />
        {proofsError && (
          <p className='text-xs text-destructive'>{proofsError}</p>
        )}

        <AttachmentPreviewGrid
          items={proofPreviewItems}
          typeBadgeClassName='font-medium'
          onRemove={item => {
            if (item.id) onRemoveProof(item.id)
          }}
          onRename={onRenameProof}
          removeLabel={t('common:actions.cancel')}
        />
      </div>

      <div className='space-y-1.5'>
        <Label className={fieldLabelClass}>{t('form.notes')}</Label>
        <Textarea
          value={notes}
          maxLength={2000}
          rows={4}
          placeholder={t('form.notesPlaceholder')}
          onChange={event => onNotesChange(event.target.value)}
          className={cn(
            'min-h-[80px] resize-y rounded-xl border-[#e7e9ef] bg-[#f4f5f8] px-4 py-3',
            textareaFieldClass,
            layout === 'invoice' && invoiceInputTypographyClass,
            fieldBorderClass,
            FORM_CONTROL_FOCUS_CLASS
          )}
        />
      </div>

      {waiting && (
        <div className='rounded-xl border border-[#dbe7ff] bg-[#eef4ff] px-3 py-3 text-xs text-foreground'>
          <p className='font-medium'>{t('form.stripeWaiting')}</p>
          <p className='mt-1 text-[#5f6673]'>{t('form.stripeWaitingHint')}</p>
          <a
            href={waiting.url}
            target='_blank'
            rel='noopener noreferrer'
            className='mt-2 inline-block font-medium text-primary hover:underline'
          >
            {t('form.stripeReopen')}
          </a>
        </div>
      )}
    </>
  )
}
